import React, { useState } from 'react';
import * as Yup from 'yup';
import { Button } from '@economic/taco';

import { t, Trans } from '@lingui/macro';
import ExternalLink from '../../shared/components/ExternalLink';
import { Field, Form } from '../../shared/components/Form';
import { OnboardingStatusSetups } from './types';
import internalApiClient from '../../shared/util/api/internalApiClient';

const validationSchema = () =>
    Yup.object().shape({
        // trick to force selecting some setup (0 is invalid), required won't work
        setupId: Yup.number().min(1, () => t`You must select a chart of account`),
    });

const onboardingCopySetup = setupId => internalApiClient.put(`/onboarding/standard-setup`, { setupId, copyLayout: true });

interface Props {
    setups: OnboardingStatusSetups;
    onBack: () => void;
    onDone: () => void;
    onSkip: () => void;
    children: string | JSX.Element;
}

export default function StepCompanyType({ setups, onBack, onDone, onSkip, children }: Props) {
    const [seeMore, setSeeMore] = useState(false);

    async function copySetup({ setupId }: any) {
        await onboardingCopySetup(setupId);
        onDone();
    }

    const chosenDefaultSetup = setups.setups.find(s => s.setup === setups.chosenDefaultSetup);
    const { canApplyDefaultSetup, errorsPreventingApplyingStandardSetup } = setups;

    // split in two groups
    const favorites = setups.setups.filter(s => s.isFavorite);
    const other = setups.setups.filter(s => !s.isFavorite);

    return (
        <div className="onboarding__container__fixed-size">
            <div className="onboarding onboarding__company-type text-center">
                {children}

                <h1>
                    <Trans>Choose a chart of account</Trans>
                </h1>
                <p>
                    <Trans>
                        This is the best way to get started in e-conomic. Don't worry if you choose the wrong one - it can be
                        adjusted to your needs later on. However we recommend not to do any bookings before you know the chart of
                        account fit your needs. Want to know what the account plans include?
                    </Trans>
                    <br />
                    <Trans>
                        Read more&nbsp;
                        <ExternalLink href="http://wiki2.e-conomic.dk/indstillinger/regnskab-standardopsaetning">
                            here.
                        </ExternalLink>
                    </Trans>
                </p>

                {chosenDefaultSetup ? (
                    <>
                        <p>
                            <Trans>A setup has already been selected:</Trans>
                            <br />
                            <strong>{chosenDefaultSetup.name}</strong>
                        </p>

                        <p>
                            <Trans>
                                If you want to change it later, you can do it from
                                <br />
                                All settings
                                <i className="long-arrow-right" />
                                Finance
                                <i className="long-arrow-right" />
                                Default setups.
                            </Trans>
                        </p>

                        <div className="onboarding__navigation-buttons">
                            <Button appearance="primary" onClick={onSkip}>
                                <Trans>Next</Trans>
                            </Button>
                            <Button appearance="discrete" onClick={onBack}>
                                <Trans>Back</Trans>
                            </Button>
                        </div>
                    </>
                ) : !canApplyDefaultSetup ? (
                    <>
                        <p>
                            <strong>
                                <Trans>You cannot select a setup for the following reasons:</Trans>
                            </strong>
                        </p>
                        <ul>
                            {errorsPreventingApplyingStandardSetup.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                        <p>
                            <strong>
                                <Trans>To select a setup, no customers, suppliers, products or entries must be created.</Trans>
                            </strong>
                        </p>
                        <div className="onboarding__navigation-buttons">
                            <Button appearance="primary" onClick={onSkip}>
                                <Trans>Next</Trans>
                            </Button>
                            <Button appearance="discrete" onClick={onBack}>
                                <Trans>Back</Trans>
                            </Button>
                        </div>
                    </>
                ) : (
                    <div>
                        <Form
                            skipNotifications
                            onSubmit={copySetup}
                            validationSchema={validationSchema}
                            validateOnBlur={false}
                            initialData={{ setupId: Number(setups.chosenDefaultSetup) }}>
                            <div className="onboarding__form-content">
                                <div className="onboarding__chart-of-account-list">
                                    <Field.NumberRadioGroup
                                        name="setupId"
                                        options={favorites.map(setup => ({ text: setup.name, value: setup.setup }))}
                                    />

                                    {other.length > 0 && (
                                        <>
                                            {!seeMore && (
                                                <Button
                                                    className="see-more"
                                                    appearance="discrete"
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setSeeMore(!seeMore);
                                                    }}>
                                                    <Trans>See more options</Trans>
                                                </Button>
                                            )}
                                            {seeMore && (
                                                <Field.NumberRadioGroup
                                                    name="setupId"
                                                    options={other.map(setup => ({ text: setup.name, value: setup.setup }))}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="onboarding__navigation-buttons">
                                <Button appearance="primary" type="submit">
                                    <Trans>Next</Trans>
                                </Button>
                                <Button
                                    appearance="discrete"
                                    onClick={e => {
                                        e.preventDefault();
                                        onBack();
                                    }}>
                                    <Trans>Back</Trans>
                                </Button>
                            </div>
                        </Form>
                    </div>
                )}
            </div>
        </div>
    );
}
